/**
 * Stable, wire-visible error taxonomy.
 *
 * `ErrorCode` is the `code` member of the RFC 7807 problem+json body every API error
 * carries. It is a permanent part of the client contract: codes may be added, never
 * renamed or repurposed — all four clients branch on them.
 *
 * This module is deliberately dependency-free (no zod, no Node builtins) so the
 * smallest possible surface can be imported by an offline outbox or a service worker.
 */

export const ERROR_CODES = [
  'unauthorized',
  'forbidden',
  'not_found',
  'validation_failed',
  'conflict',
  'idempotency_replay',
  'budget_exhausted',
  'rate_limited',
  'device_revoked',
  'share_scope_violation',
  'internal',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

/**
 * The oRPC error-map keys the codes are transported as. oRPC uses SCREAMING_SNAKE
 * keys and derives a default HTTP status from the well-known ones; the custom ones
 * carry an explicit status in `CONTRACT_ERROR_MAP` (see `router.ts`).
 */
export type OrpcErrorKey =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION_FAILED'
  | 'CONFLICT'
  | 'IDEMPOTENCY_REPLAY'
  | 'BUDGET_EXHAUSTED'
  | 'RATE_LIMITED'
  | 'DEVICE_REVOKED'
  | 'SHARE_SCOPE_VIOLATION'
  | 'INTERNAL_SERVER_ERROR';

export interface ErrorDefinition {
  /** HTTP status the API returns for this code. */
  readonly status: number;
  /**
   * Whether a client may replay the identical request with backoff.
   * The api-client's retry policy reads exactly this flag.
   */
  readonly retryable: boolean;
  /** Short human title; becomes the problem+json `title`. */
  readonly title: string;
  /** Stable problem-type URI reference; becomes the problem+json `type`. */
  readonly type: string;
  /** The oRPC error-map key this code travels as. */
  readonly orpcCode: OrpcErrorKey;
}

/** Namespace for problem-type URIs. Stable; do not change once published. */
export const PROBLEM_TYPE_BASE = 'https://crm.example/problems';

export const ERROR_DEFS = {
  unauthorized: {
    status: 401,
    retryable: false,
    title: 'Unauthorized',
    type: `${PROBLEM_TYPE_BASE}/unauthorized`,
    orpcCode: 'UNAUTHORIZED',
  },
  forbidden: {
    status: 403,
    retryable: false,
    title: 'Forbidden',
    type: `${PROBLEM_TYPE_BASE}/forbidden`,
    orpcCode: 'FORBIDDEN',
  },
  not_found: {
    status: 404,
    retryable: false,
    title: 'Not Found',
    type: `${PROBLEM_TYPE_BASE}/not-found`,
    orpcCode: 'NOT_FOUND',
  },
  validation_failed: {
    status: 422,
    retryable: false,
    title: 'Validation Failed',
    type: `${PROBLEM_TYPE_BASE}/validation-failed`,
    orpcCode: 'VALIDATION_FAILED',
  },
  conflict: {
    status: 409,
    retryable: false,
    title: 'Conflict',
    type: `${PROBLEM_TYPE_BASE}/conflict`,
    orpcCode: 'CONFLICT',
  },
  /**
   * The same `Idempotency-Key` was replayed with a *different* payload. A replay with
   * an identical payload is not an error — the server returns the stored response.
   */
  idempotency_replay: {
    status: 409,
    retryable: false,
    title: 'Idempotency Key Reused',
    type: `${PROBLEM_TYPE_BASE}/idempotency-replay`,
    orpcCode: 'IDEMPOTENCY_REPLAY',
  },
  /** Enrichment credit cap or LLM token cap exhausted for the period. */
  budget_exhausted: {
    status: 402,
    retryable: false,
    title: 'Budget Exhausted',
    type: `${PROBLEM_TYPE_BASE}/budget-exhausted`,
    orpcCode: 'BUDGET_EXHAUSTED',
  },
  rate_limited: {
    status: 429,
    retryable: true,
    title: 'Rate Limited',
    type: `${PROBLEM_TYPE_BASE}/rate-limited`,
    orpcCode: 'RATE_LIMITED',
  },
  /** The device api-key / DPoP key was revoked; the client must re-pair, not retry. */
  device_revoked: {
    status: 401,
    retryable: false,
    title: 'Device Revoked',
    type: `${PROBLEM_TYPE_BASE}/device-revoked`,
    orpcCode: 'DEVICE_REVOKED',
  },
  /** The caller has access to the record but not at the field class it asked for. */
  share_scope_violation: {
    status: 403,
    retryable: false,
    title: 'Share Scope Violation',
    type: `${PROBLEM_TYPE_BASE}/share-scope-violation`,
    orpcCode: 'SHARE_SCOPE_VIOLATION',
  },
  internal: {
    status: 500,
    retryable: true,
    title: 'Internal Server Error',
    type: `${PROBLEM_TYPE_BASE}/internal`,
    orpcCode: 'INTERNAL_SERVER_ERROR',
  },
} as const satisfies Record<ErrorCode, ErrorDefinition>;

/**
 * The canonical application error body. Mirrors the problem+json payload minus the
 * RFC 7807 envelope fields, which the transport layer adds.
 */
export interface AppError {
  code: ErrorCode;
  message: string;
  retryable: boolean;
  details?: unknown;
  correlationId: string;
}

/** RFC 7807 problem+json response body. */
export interface ProblemDetails extends AppError {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
}

const ERROR_CODE_SET: ReadonlySet<string> = new Set<string>(ERROR_CODES);

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && ERROR_CODE_SET.has(value);
}

export function statusForErrorCode(code: ErrorCode): number {
  return ERROR_DEFS[code].status;
}

export function isRetryableErrorCode(code: ErrorCode): boolean {
  return ERROR_DEFS[code].retryable;
}

export function orpcCodeForErrorCode(code: ErrorCode): OrpcErrorKey {
  return ERROR_DEFS[code].orpcCode;
}

const ERROR_CODE_BY_ORPC_CODE: ReadonlyMap<string, ErrorCode> = new Map(
  ERROR_CODES.map((code): [string, ErrorCode] => [ERROR_DEFS[code].orpcCode, code]),
);

/** Reverse lookup used by clients that only see the oRPC error key. */
export function errorCodeForOrpcCode(orpcCode: string): ErrorCode {
  return ERROR_CODE_BY_ORPC_CODE.get(orpcCode) ?? 'internal';
}

export interface AppErrorInit {
  details?: unknown;
  /** Overrides the default retryability of the code (e.g. a retryable 500 sub-case). */
  retryable?: boolean;
}

/** Builds a well-formed `AppError`, filling `retryable` from the code's definition. */
export function appError(
  code: ErrorCode,
  message: string,
  correlationId: string,
  init: AppErrorInit = {},
): AppError {
  const error: AppError = {
    code,
    message,
    retryable: init.retryable ?? ERROR_DEFS[code].retryable,
    correlationId,
  };
  if (init.details !== undefined) {
    error.details = init.details;
  }
  return error;
}

/** Narrows an unknown value (e.g. a decoded error body) to an `AppError`. */
export function isAppError(value: unknown): value is AppError {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    isErrorCode(candidate.code) &&
    typeof candidate.message === 'string' &&
    typeof candidate.retryable === 'boolean' &&
    typeof candidate.correlationId === 'string'
  );
}

/** Expands an `AppError` into a full RFC 7807 body. */
export function toProblemDetails(error: AppError, instance?: string): ProblemDetails {
  const def = ERROR_DEFS[error.code];
  const problem: ProblemDetails = {
    ...error,
    type: def.type,
    title: def.title,
    status: def.status,
  };
  if (instance !== undefined) {
    problem.instance = instance;
  }
  return problem;
}
